import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";

export function useSetStar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ symbol, starred }: { symbol: string; starred: boolean }) =>
      api.setStar(symbol, starred),
    onSuccess: (_data, { symbol }) => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["company", symbol] });
      qc.invalidateQueries({ queryKey: ["earnings-upcoming"] });
      qc.invalidateQueries({ queryKey: ["earnings-by-month"] });
    },
  });
}

export function useCompanies(query?: string) {
  return useQuery({
    queryKey: ["companies", query ?? ""],
    queryFn: () => api.listCompanies(query),
  });
}

export function useCompany(symbol: string | null) {
  return useQuery({
    queryKey: ["company", symbol],
    queryFn: () => api.getCompany(symbol as string),
    enabled: !!symbol,
  });
}

export function usePrices(symbol: string | null, start?: string, end?: string) {
  return useQuery({
    queryKey: ["prices", symbol, start, end],
    queryFn: () => api.getPrices(symbol as string, start, end),
    enabled: !!symbol,
  });
}

export function useCompanyEarnings(symbol: string | null) {
  return useQuery({
    queryKey: ["company-earnings", symbol],
    queryFn: () => api.listEarnings(symbol as string),
    enabled: !!symbol,
  });
}

export function useCompanyArticles(symbol: string | null, date: string | null) {
  return useQuery({
    queryKey: ["company-articles", symbol, date],
    queryFn: () => api.listArticlesForCompany(symbol as string, date as string),
    enabled: !!symbol && !!date,
  });
}

export function useModels() {
  return useQuery({
    queryKey: ["models"],
    queryFn: () => api.listModels(),
  });
}

export function useModelRuns(slug: string | null, date?: string) {
  return useQuery({
    queryKey: ["model-runs", slug, date],
    queryFn: () => api.listModelRuns(slug as string, date),
    enabled: !!slug,
  });
}

export function useRunPredictions(slug: string | null, runId: number | null) {
  return useQuery({
    queryKey: ["run-predictions", slug, runId],
    queryFn: () => api.listRunPredictions(slug as string, runId as number),
    enabled: !!slug && runId !== null,
  });
}

export function useNewsSources(query?: string) {
  return useQuery({
    queryKey: ["news-sources", query ?? ""],
    queryFn: () => api.listNewsSources(query),
  });
}

export function useSourceArticles(
  sourceId: number | null,
  opts?: { q?: string; companyId?: number },
) {
  return useQuery({
    queryKey: ["source-articles", sourceId, opts?.q ?? "", opts?.companyId ?? null],
    queryFn: () => api.listSourceArticles(sourceId as number, opts),
    enabled: sourceId !== null,
  });
}

export function useUpcomingEarnings(limit = 50) {
  return useQuery({
    queryKey: ["earnings-upcoming", limit],
    queryFn: () => api.listUpcomingEarnings(limit),
  });
}

export function useEarningsByMonth(year: number, month: number) {
  return useQuery({
    queryKey: ["earnings-by-month", year, month],
    queryFn: () => api.listEarningsByMonth(year, month),
  });
}

export function useCompanyArticlesRange(
  symbol: string | null,
  start: string | null,
  end: string | null,
  sourceIds: number[],
  order: "asc" | "desc",
) {
  const sortedSources = [...sourceIds].sort((a, b) => a - b);
  return useQuery({
    queryKey: ["company-articles-range", symbol, start, end, sortedSources, order],
    queryFn: () =>
      api.listArticlesForCompanyRange(symbol as string, start as string, end as string, {
        sourceIds: sortedSources,
        order,
      }),
    enabled: !!symbol && !!start && !!end,
  });
}
